import * as React from 'react';
import { useCallback, useMemo } from 'react';
import cn from 'classnames';

import { useTheme } from '../../theme';
import { Aircraft, Airline, FlightAmenityParameter, FlightMarkersInfo, TitleValueParameter } from '@websky/graphql';
import Amenities from '../../Amenities';
import { Time } from '../../Icons';
import AircraftPopup from '../../AircraftPopup';
import { useIataCode } from '../../hooks';
import ImageWithFallback from '../../ImageWithFallback';
import { useRenderers } from '../../renderProps';

export interface Props {
	airline: Airline;
	flightNumber: string;
	duration: string;
	aircraft: Aircraft;
	statistics?: TitleValueParameter[];
	amenities?: FlightAmenityParameter[];
	markers?: FlightMarkersInfo;
	className?: string;
	isReturnFlight?: boolean;
}

const FlightCard: React.FC<Props> = ({
	airline,
	flightNumber,
	duration,
	aircraft,
	amenities,
	statistics,
	className,
	markers,
	isReturnFlight
}) => {
	const { FlightCard: css } = useTheme('FlightCard');
	const hasAmenities = amenities?.length > 0;
	const iataCode = useIataCode();

	const hideAmenitiesIfAllUnable = ['WZ', 'ZF', 'RM', 'A9']; // в темах этих ак внутри Amenities не отображем элементы с available: false
	const hasAvailableAmenity = amenities?.some(amenity => amenity.available) || markers?.eco;
	const hideAmenities = !hasAmenities || (!hasAvailableAmenity && hideAmenitiesIfAllUnable.includes(iataCode));

	const renderButton = useCallback(
		(onClick: () => void) => (
			<div
				className={cn(css.aircraft__name, {
					[css.aircraft__name_active]: !!aircraft?.id,
					[css.aircraft__name_withNoAmenities]: hideAmenities
				})}
				onClick={aircraft?.id ? onClick : null}
			>
				<span className={css.aircraft__nameTitle}>{aircraft?.name}</span>
			</div>
		),
		[aircraft, statistics, hideAmenities]
	);

	const renderAircraftImage = useCallback(
		(onClick: () => void) => (
			<div
				className={cn(css.aircraft, {
					[css.aircraft_image_active]: !!aircraft?.id,
					[css.aircraft_return]: isReturnFlight
				})}
				onClick={aircraft?.id ? onClick : null}
			>
				<ImageWithFallback
					className={css.aircraft__image}
					src={aircraft?.icon}
					fallbackSrc={'https://cdn.websky.aero/content/default-content/images/Aircrafts/738/738.png '}
					alt={aircraft?.name}
				/>
			</div>
		),
		[aircraft, isReturnFlight]
	);

	const { renderFLightCardCarrierName } = useRenderers();

	const renderAirlineName = useMemo(() => {
		if (renderFLightCardCarrierName) {
			return renderFLightCardCarrierName({ airline, aircraft, flightNumber, duration });
		} else {
			return (
				<>
					<span className={css.airline__number}>{flightNumber}</span>
					<span className={css.airline__name}>{airline.name}</span>
				</>
			);
		}
	}, [airline.name]);

	return (
		<div className={cn(css.wrapper, className)}>
			<div className={css.header}>
				<div className={css.airline}>{renderAirlineName}</div>

				<div className={css.flight}>
					{Time}
					{duration}
				</div>
			</div>

			<div className={cn(css.aircraft, { [css.aircraft_return]: isReturnFlight })}>
				<AircraftPopup
					id={aircraft?.id}
					operatingIata={airline.iata}
					name={aircraft?.name}
					renderButton={renderAircraftImage}
				/>
			</div>

			<div className={css.footer}>
				<AircraftPopup
					id={aircraft?.id}
					operatingIata={airline.iata}
					name={aircraft?.name}
					renderButton={renderButton}
				/>

				{!hideAmenities && <Amenities className={css.amenities} amenities={amenities} isEco={markers?.eco} />}
			</div>
		</div>
	);
};

export default FlightCard;
